/**
 * COP Featured Product — Thumbnail Carousel & Size Chart Modal
 *
 * Custom elements:
 *   <cop-thumbnail-carousel> — Horizontal thumbnail strip with prev/next navigation
 *
 * Also wires up the size-chart trigger/close buttons.
 */

/* ============================================
   Thumbnail Carousel
   ============================================ */
class CopThumbnailCarousel extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.scrollAmount = 90; // thumbnail width + gap
  }

  connectedCallback() {
    this.thumbnailContainer = this.querySelector('.cop-fp__thumbnails');
    this.thumbnails = this.querySelectorAll('.cop-fp__thumbnail');
    this.prevBtn = this.querySelector('.cop-fp__thumb-nav--prev');
    this.nextBtn = this.querySelector('.cop-fp__thumb-nav--next');
    this.mainImage = document.getElementById(`CopMainImg-${this.sectionId}`);

    if (!this.thumbnailContainer || this.thumbnails.length === 0) return;

    // Thumbnail click → swap main image
    this.thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', () => this.onThumbnailClick(thumb));
    });

    // Navigation arrows
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scroll('prev'));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scroll('next'));
    }

    // Update arrow states on scroll
    this.thumbnailContainer.addEventListener('scroll', () => this.updateArrows());

    // Initial arrow state
    this.updateArrows();
  }

  onThumbnailClick(thumb) {
    // Update active state
    this.thumbnails.forEach((t) => t.classList.remove('cop-fp__thumbnail--active'));
    thumb.classList.add('cop-fp__thumbnail--active');

    // Swap main image
    if (this.mainImage && thumb.dataset.mediaSrc) {
      this.mainImage.src = thumb.dataset.mediaSrc;
      this.mainImage.alt = thumb.getAttribute('aria-label') || '';
    }

    this.updateArrows();
  }

  scroll(direction) {
    const active = this.querySelector('.cop-fp__thumbnail--active');
    const thumbsArray = Array.from(this.thumbnails);
    const currentIndex = active ? thumbsArray.indexOf(active) : 0;
    const nextIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

    if (nextIndex < 0 || nextIndex >= thumbsArray.length) return;

    const nextThumb = thumbsArray[nextIndex];
    this.onThumbnailClick(nextThumb);

    // Scroll the thumbnail into view
    nextThumb.scrollIntoView({ inline: 'nearest', behavior: 'smooth', block: 'nearest' });

    this.updateArrows();
  }

  updateArrows() {
    if (!this.thumbnailContainer) return;

    const active = this.querySelector('.cop-fp__thumbnail--active');
    const thumbsArray = Array.from(this.thumbnails);
    const currentIndex = active ? thumbsArray.indexOf(active) : 0;

    if (this.prevBtn) {
      this.prevBtn.disabled = currentIndex <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = currentIndex >= thumbsArray.length - 1;
    }
  }
}

if (!customElements.get('cop-thumbnail-carousel')) {
  customElements.define('cop-thumbnail-carousel', CopThumbnailCarousel);
}

/* ============================================
   Disable unavailable variant pills
   Sets the actual `disabled` attribute on radio
   inputs with Dawn's `.disabled` class so they
   are truly non-interactive (not just styled).
   Runs on load and re-runs whenever variant-selects
   re-renders the options (via MutationObserver).
   ============================================ */
function disableUnavailablePills(container) {
  if (!container) return;
  container.querySelectorAll('.cop-fp__pills input[type="radio"].disabled').forEach((input) => {
    input.disabled = true;
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.cop-fp__variants').forEach((wrapper) => {
    // Initial pass
    disableUnavailablePills(wrapper);

    // Watch for Dawn's JS re-rendering options on variant change
    const observer = new MutationObserver(() => disableUnavailablePills(wrapper));
    observer.observe(wrapper, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  });
});

/* ============================================
   Size Chart Modal
   ============================================ */
document.addEventListener('DOMContentLoaded', () => {
  // Re-parent modals to <body> so ancestor stacking contexts / transforms
  // can't constrain their fixed positioning.
  document.querySelectorAll('.cop-fp__size-chart-modal').forEach((modal) => {
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  });

  // Open triggers
  document.querySelectorAll('[data-size-chart-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const section = trigger.closest('.cop-featured-product, .cop-product-wrapper');
      const sectionId = section ? section.dataset.section : null;
      const modal = sectionId
        ? document.getElementById(`SizeChartModal-${sectionId}`)
        : document.querySelector('.cop-fp__size-chart-modal');
      if (modal) {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        const closeBtn = modal.querySelector('.cop-fp__size-chart-close');
        if (closeBtn) closeBtn.focus();
      }
    });
  });

  // Close triggers
  document.querySelectorAll('[data-size-chart-close]').forEach((closer) => {
    closer.addEventListener('click', () => {
      const modal = closer.closest('.cop-fp__size-chart-modal');
      if (modal) {
        modal.hidden = true;
        document.body.style.overflow = '';
      }
    });
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.cop-fp__size-chart-modal:not([hidden])').forEach((modal) => {
        modal.hidden = true;
        document.body.style.overflow = '';
      });
    }
  });
});
